import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormDatePicker } from '../../../components/Form/FormDatePicker';
import { useCasePermissions } from '../../../hooks/UseCasePermission';

import type { User } from '../../../types/user';
import { useTableFilters } from '../../../hooks/UseTableFilter';
import { TablePagination } from '../../../components/common/TablePagination';
import { deleteLegalAdviceForm, fetchLegalAdviceApplications } from '../../../services/LegalAdviceFourService';

export function LegalFormFourTable() {
  const navigate = useNavigate();
  const itemsPerPage = 10;

  const filters = useTableFilters();

  const [currentUser] = useState<User | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  });

  useCasePermissions(currentUser);

  const { data, isLoading, isError, error, isPlaceholderData, refetch, isFetching } = useQuery({
    queryKey: [
      'legalFormFour',
      filters.debouncedSearch,
      filters.debouncedCourt,
      filters.debouncedParty,
      filters.selectedStatus,
      filters.selectedDate,
      filters.currentPage,
    ],
    queryFn: () =>
      fetchLegalAdviceApplications({
        searchTerm: filters.debouncedSearch,
        courtSearch: filters.debouncedCourt,
        partySearch: filters.debouncedParty,
        selectedStatus: filters.selectedStatus,
        selectedDate: filters.selectedDate,
        currentPage: filters.currentPage,
        itemsPerPage,
      }),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const rawApplications = data?.data || [];

  const checkIsOriginUser = (app: any): boolean => {
    if (!currentUser) return false;

    // 1. Match by Office ID (Top-level originOfficeId or nested originOffice.id)
    const originOfficeId = app.originOfficeId || app.originOffice?.id || app.document?.originOfficeId;
    if (currentUser.officeId && originOfficeId && currentUser.officeId === originOfficeId) {
      return true;
    }

    // 2. Match by Creator User ID
    const createdById = app.createdById || app.createdBy?.id;
    if (currentUser.id && createdById && currentUser.id === createdById) {
      return true;
    }

    return false;
  };

  const applications = rawApplications.filter((app: any) => {
    const isOriginUser = checkIsOriginUser(app);

    if (filters.selectedStatus === 'SUBMITTED') {
      return isOriginUser; // Only show documents sent/created by current user's office
    }
    if (filters.selectedStatus === 'RECEIVED') {
      return !isOriginUser; // Only show documents received from other offices
    }
    return true;
  });

  const totalItems = data?.totalItems || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handleDelete = async (id?: string) => {
    if (!id) return;

    const confirmed = window.confirm('ဤမှတ်တမ်းကို ဖျက်ရန် သေချာပါသလား။');
    if (!confirmed) return;

    try {
      await deleteLegalAdviceForm(id);
      await refetch();
    } catch (err: any) {
      console.error('Delete failed:', err);
      alert(err?.message || 'ဖျက်ခြင်း မအောင်မြင်ပါ။');
    }
  };

  return (
    <div className="space-y-4 bg-white p-4 md:p-6 rounded-2xl border border-slate-200 shadow-sm">
      {/* Table Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight">
            ဥပရအမှုလိုက်ပုံစံ (၄) စာရင်းများ
          </h2>
          {/* <p className="mt-1 text-xs text-slate-500">
            {isUpperUser
              ? 'အောက်ခြေရုံးများနှင့် ရဲစခန်းများမှ ပေးပို့လာသော ဥပဒေအကြံတောင်းခံလွှာများ စိစစ်အတည်ပြုခြင်းနှင့် စီမံခန့်ခွဲမှု။'
              : 'ဥပဒေအကြံတောင်းခံလွှာ ရှာဖွေစစ်ထုတ်ခြင်းနှင့် အကြံပြုချက် မှတ်တမ်းများ။'}
          </p> */}
        </div>

        {/* Action Buttons Group */}
        <div className="flex items-center gap-2 self-start sm:self-center shrink-0">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition-colors disabled:opacity-50"
            title="ဒေတာများ နောက်ဆုံး Data များ ရယူရန် "
          >
            <svg
              className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin text-blue-600' : 'text-slate-500'}`}
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
              />
            </svg>
            နောက်ဆုံး Data များ ရယူရန် 
          </button>

          <button
            type="button"
            onClick={() => navigate('/legal-form-four/create')}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-blue-500 transition-colors shrink-0"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            ဖောင်အသစ်ထည့်ရန်
          </button>
        </div>
      </div>

      {/* Filter Controls Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 items-end bg-slate-50/60 p-3 rounded-xl border border-slate-100">
        <div className="lg:col-span-2">
          <label className="block text-[11px] font-medium text-slate-500 mb-1">အထွေထွေ ရှာဖွေရန်</label>
          <input
            type="text"
            placeholder="ဥပရမှတ်ပုံတင်/တရားရုံးအမှုအမှတ်/ရဲစခန်း..."
            value={filters.searchTerm}
            onChange={(e) => {
              filters.setSearchTerm(e.target.value);
              filters.setCurrentPage(1);
            }}
            className="w-full rounded-md border border-slate-200 bg-white py-1.5 px-2.5 text-xs text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">အခြေအနေ</label>
          <select
            value={filters.selectedStatus}
            onChange={(e) => {
              filters.setSelectedStatus(e.target.value);
              filters.setCurrentPage(1);
            }}
            className="w-full rounded-md border border-slate-200 bg-white py-1.5 px-2.5 text-xs text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">အခြေအနေ အားလုံး</option>
            <option value="DRAFT">မူကြမ်း</option>
            <option value="APPROVED">အတည်ပြုပြီး</option>
            <option value="SUBMITTED">တင်ပြပြီး</option>
            <option value="RECEIVED">လက်ခံရရှိသည်</option>
            <option value="UNDER_REVIEW">ကြည့်ရှုထားသည်</option>
            <option value="ATTACHED">ညွန်ကြားထားသည် </option>
          </select>
        </div>

        <div className="flex items-center gap-1.5">
          <div className="flex-1">
            <label className="block text-[11px] font-medium text-slate-500 mb-1">ရက်စွဲ</label>
            <FormDatePicker
              value={filters.selectedDate}
              onChange={(e) => {
                filters.setSelectedDate(e.target.value);
                filters.setCurrentPage(1);
              }}
              name="selectedDate"
              className="w-full"
            />
          </div>

          {filters.hasActiveFilters && (
            <button
              type="button"
              onClick={filters.handleClearFilters}
              title="စစ်ထုတ်မှုများ ဖျက်ရန်"
              className="mt-5 shrink-0 inline-flex items-center justify-center text-xs font-medium text-rose-600 hover:bg-rose-50 border border-rose-200 rounded-md py-1.5 px-2 bg-white transition-colors"
            >
              🔄
            </button>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm relative">
        {isPlaceholderData && (
          <div className="absolute inset-0 bg-white/40 backdrop-blur-[1px] flex items-center justify-center z-10">
            <span className="text-xs bg-slate-800 text-white px-2.5 py-1 rounded-full shadow-sm">
              နောက်ဆုံးပေါ်အချက်အလက် ရယူနေပါသည်...
            </span>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full table-fixed text-center border-collapse text-xs min-w-[1528px]">
            <colgroup>
              <col className="w-14" />
              <col className="w-36" />
              <col className="w-40" />
              <col className="w-36" />
              <col className="w-40" />
              <col className="w-48" />
              <col className="w-56" />
              <col className="w-56" />
              <col className="w-40" />
              <col className="w-16" />
            </colgroup>
            <thead className="bg-slate-50 text-[11px] font-semibold text-black">
              <tr>
                <th rowSpan={2} className="border-r border-b border-black p-2">အမှတ် စဉ်</th>
                <th rowSpan={2} className="border-r border-b border-black p-2">ဥပရ မှတ်ပုံတင် ( ) အမှတ်စဉ်</th>
                <th rowSpan={2} className="border-r border-b border-black p-2">စွဲတင်သည့် ရက်စွဲနှင့် ဥပဒေပုဒ်မ</th>
                <th rowSpan={2} className="border-r border-b border-black p-2">တရားရုံး အမှုအမှတ်</th>
                <th rowSpan={2} className="border-r border-b border-black p-2">ရဲစခန်းအမည်နှင့် (ပ) အမှတ်</th>
                <th rowSpan={2} className="border-r border-b border-black p-2">အမိန့်ချသည့် ရက်စွဲနှင့် အမိန့်အကျဉ်း</th>
                <th colSpan={2} className="border-r border-b border-black p-2">အယူခံမှု၊ပြင်ဆင်မှု အထွေထွေမှုတက်ရန် ရှိ/မရှိ မှတ်ချက်</th>
                <th rowSpan={2} className="border-r border-b border-black p-2">အမှုလိုက် ဥပဒေအရာရှိ အမည်</th>
                <th rowSpan={2} className="border-b border-black p-2 font-sans">လုပ်ဆောင်ချက်</th>
              </tr>
              <tr>
                <th className="border-r border-b border-black p-2">အမှုလိုက် ဥပဒေအရာရှိ</th>
                <th className="border-r border-b border-black p-2">တာဝန်ခံ ဥပဒေအရာရှိ</th>
              </tr>
              <tr className="h-7 border-b border-black text-[10px] font-semibold text-slate-600">
                {['(၁)', '(၂)', '(၃)', '(၄)', '(၅)', '(၆)', '(၇)', '(၈)', '(၉)'].map((number) => (
                  <th key={number} className="border-r border-black px-2 py-1">{number}</th>
                ))}
                <th className="p-1" aria-hidden="true" />
              </tr>
            </thead>
            <tbody className="bg-white text-slate-900">
              {isLoading && !isPlaceholderData ? (
                <tr>
                  <td colSpan={10} className="text-center py-12 text-xs text-slate-400">
                    အချက်အလက်များ ဆွဲယူနေပါသည်...
                  </td>
                </tr>
              ) : isError ? (
                <tr>
                  <td colSpan={10} className="text-center py-12 text-xs text-red-500">
                    {error instanceof Error ? error.message : 'အချက်အလက်များ ဆွဲယူရာတွင် အမှားအယွင်းရှိခဲ့ပါသည်။'}
                  </td>
                </tr>
              ) : applications.length > 0 ? (
                applications.map((app: any, index: number) => {
                  const rowId = app.id || app._id;
                  const formDetails = app.legalAdviceFormFour || app.legalAdviceForm || app.formData || app;
                  const isOriginUser = checkIsOriginUser(app);

                  return (
                    <tr key={rowId || index} className="border-b border-black hover:bg-slate-50 align-top">
                      <td className="border-r border-black p-2 text-center font-bold align-middle">
                        {formDetails.srNo || (filters.currentPage - 1) * itemsPerPage + index + 1}
                      </td>
                      <td className="border-r border-black p-1.5 align-top font-semibold text-blue-600"><div className="min-h-20 whitespace-pre-wrap break-words text-left">{formDetails.lawRegisterNo || '-'}</div></td>
                      <td className="border-r border-black p-1.5 align-top"><div className="min-h-20 whitespace-pre-wrap break-words text-left">{formDetails.dateAndSection || '-'}</div></td>
                      <td className="border-r border-black p-1.5 align-top"><div className="min-h-20 whitespace-pre-wrap break-words text-left">{formDetails.courtAndCaseNo || '-'}</div></td>
                      <td className="border-r border-black p-1.5 align-top"><div className="min-h-20 whitespace-pre-wrap break-words text-left">{formDetails.stationAndCrNo || '-'}</div></td>
                      <td className="border-r border-black p-1.5 align-top"><div className="min-h-20 whitespace-pre-wrap break-words text-left">{formDetails.orderDateAndSummary || '-'}</div></td>
                      <td className="border-r border-black p-1.5 align-top"><div className="min-h-20 whitespace-pre-wrap break-words text-left">{formDetails.advocateOpinion || '-'}</div></td>
                      <td className="border-r border-black p-1.5 align-top"><div className="min-h-20 whitespace-pre-wrap break-words text-left">{formDetails.officerOpinion || '-'}</div></td>
                      <td className="border-r border-black p-1.5 align-top"><div className="min-h-20 whitespace-pre-wrap break-words text-left">{formDetails.advocateName || '-'}</div></td>
                      <td className="p-1 text-center font-sans">
                        {isOriginUser ? (
                          <div className="flex justify-center gap-1 items-center">
                            <button
                              type="button"
                              onClick={() => navigate(`/legal-form-four/edit/${app.documentNumber}`)}
                              className="text-blue-600 hover:text-blue-800 font-medium text-xs focus:outline-none"
                              title="ပြင်ဆင်ရန်"
                            >
                              Edit
                            </button>
                            <span className="text-gray-300">|</span>
                            <button
                              type="button"
                              onClick={() => handleDelete(app.documentNumber)}
                              className="text-red-600 hover:text-red-800 font-medium text-xs focus:outline-none"
                              title="ဖျက်ရန်"
                            >
                              Delete
                            </button>
                          </div>
                        ) : (
                          <span className="text-slate-400 font-bold">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={10} className="text-center py-12 text-xs text-slate-400">
                    ကိုက်ညီသော အချက်အလက် ရှာမတွေ့ပါ။
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && totalPages > 1 && (
          <TablePagination
            currentPage={filters.currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={filters.setCurrentPage}
          />
        )}
      </div>
    </div>
  );
}